import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useCurrency } from "../Context/CurrencyContext";


const PlanList = ({ category }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categoryName, setCategoryName] = useState("Category");
  const [allFeatures, setAllFeatures] = useState([]);
  const [showAllFeatures, setShowAllFeatures] = useState({});
  const { currency, conversionRate, loading: currencyLoading } = useCurrency();

  const routerLocation = useLocation();
  const fullPath = routerLocation.pathname;

  const slug = fullPath.replace("/", "");
  const parts = slug.split("-");

  const serverIndex = parts.lastIndexOf("server");

  const currentLocation = serverIndex !== -1 ? parts.slice(serverIndex + 1).join(" ") : '';

  const isGpuDedicatedServer = slug.startsWith("gpu-dedicated-server-");
  const isGpuDedicatedServerIndia = slug.startsWith("gpu-cloud-server-india");
  const isAnyGpuServer = slug.startsWith("gpu-dedicated-server-") || slug.startsWith("gpu-cloud-server-india");


  useEffect(() => {
    if (category) fetchPlans();
  }, [category]);


  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_API_URL}/admin/get-web-plans?category=${encodeURIComponent(
          category
        )}`
      );

      const fetchedPlans = res?.data?.plans || [];
      setPlans(fetchedPlans);

      const featureMap = new Map();
      fetchedPlans.forEach((plan) => {
        plan.features.forEach((feature) => {
         const normalizedFeatureName = feature.name.trim().toLowerCase();
          if (!featureMap.has(normalizedFeatureName)) {
            featureMap.set(normalizedFeatureName, feature);
          }
        });
      });
      setAllFeatures(Array.from(featureMap.values()));

      if (fetchedPlans.length > 0) {
        setCategoryName(
          fetchedPlans[0]?.category?.name || category || "Category"
        );
      } else {
        setCategoryName(category || "Category");
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setError("Failed to load plans. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllFeatures = (planId) => {
    setShowAllFeatures((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));
  };

  const calculatePrice = (price, duration) => {
    switch (duration) {
      case "month":
        return price;
      case "year":
        return price * 12;
      default:
        return price;
    }
  };

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltips = Array.from(tooltipTriggerList).map(
      (el) =>
        new window.bootstrap.Tooltip(el, {
          trigger: "click",
        })
    );

    const handleOutsideClick = (e) => {
      tooltips.forEach((tooltip) => {
        const tooltipEl = tooltip._element;
        if (tooltipEl && !tooltipEl.contains(e.target)) {
          tooltip.hide();
        }
      });
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        tooltips.forEach((tooltip) => tooltip.hide());
      }
    };

    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
      tooltips.forEach((tooltip) => tooltip.dispose());
    };
  }, [plans, showAllFeatures]);


  if (loading || currencyLoading)
    return <p className="text-center py-8 text-white">Loading plans...</p>;
  if (error)
    return <p className="text-center text-red-600 py-8">{error}</p>;

  const isYearlyCategory =
    category?.toLowerCase() === "microsoft-365" ||
    category?.toLowerCase() === "ssl"||
    category?.toLowerCase() === "wordpress-hosting";
    const duration = isYearlyCategory ? "year" : "month";

  const standardPlans = plans.filter(plan => plan.planType === 'standard');
  const customizedPlans = plans.filter(plan => plan.planType === 'customized');

  const filteredCustomizedPlans = customizedPlans.filter((plan) => {
    return (
      plan.category?.name?.toLowerCase() === category.toLowerCase() &&
      plan.location?.toLowerCase() === currentLocation
    );
  });
  
  return (
    <div className="container plan-container">
      {standardPlans.length > 3 && (
        <div className="d-flex justify-content-center gap-4 mb-6 flex-wrap">
          <button onClick={() => setSelectedPlan(null)} className={`px-3 py-1 border rounded transition ${ selectedPlan === null ? "bg-primary text-white" : "bg-secondary hover:bg-info text-black" }`} > All </button>

          {standardPlans.map((plan) => (
            <button key={plan._id} onClick={() => setSelectedPlan(plan)} className={`px-3 py-1 border rounded transition ${ selectedPlan?._id === plan._id ? "bg-primary text-white" : "bg-secondary hover:bg-info text-black" }`} > {plan.title} </button>
          ))}
        </div>
      )}

      <div className={`row ${ selectedPlan ? "justify-content-center" : "" }`}>
        {(selectedPlan ? [selectedPlan] : standardPlans.slice(0, 3)).map((plan) => {
          const basePrice = calculatePrice(plan.price, duration);
          const price = Math.round(basePrice * conversionRate);
          const symbol = currency === "INR" ? "₹" : "$";

          const baseCutOffPrice = calculatePrice(plan.cutOffPrice, duration);
          const cutOffPrice = Math.round(baseCutOffPrice * conversionRate);

          return (
            <div key={plan._id} className="col-xl-4 col-lg-6 col-md-6 fadeInUp">
              <div className="pricing-items">
                <div className="tag"> <h6>{plan.title || "N/A"}</h6> </div>
                <div className="pricing-header">
                  <div className="icon"> <img src="images/icon-2.png" alt="icon" /> </div>
                  <h2>{symbol}{price.toLocaleString()}<span className="less-font-weight">/</span> <span className="month">{duration.charAt(0).toUpperCase() + duration.slice(1)}</span></h2>
                  {plan.discount > 0 ? (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="month text-success"> SAVE {plan.discount}% </span>
                      <del className="price-text"> {symbol} {cutOffPrice.toLocaleString()} </del>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="month text-danger"> No Discount </span>
                      <span className="price-text"> {symbol} {cutOffPrice.toLocaleString()} </span>
                    </div>
                  )}
                  <p>{plan.shortDescription}</p>
                </div>

                <ul className="pricing-list">
                  {[
                    ...plan.features,
                    ...allFeatures.filter(
                      (f) => !plan.features.some((pf) => pf.name === f.name)
                    ),
                  ]
                    .slice(
                      0,
                      showAllFeatures[plan._id]
                        ? plan.features.length +
                            allFeatures.filter(
                              (f) => !plan.features.some((pf) => pf.name === f.name)
                            ).length
                        : 5
                    )
                    .map((feature, idx) => {
                      const normalizedFeatureName = feature.name.trim().toLowerCase();
                      const isIncluded = plan.features.some(
                        (f) => f.name.trim().toLowerCase() === normalizedFeatureName
                      );

                      const hasNumber = /\d/.test(feature.name);
                      if (!isIncluded && hasNumber) {
                        return null;
                      }
                      const planFeature = plan.features.find(
                        (f) => f.name === feature.name
                      );
                      const description = planFeature?.description?.trim();

                      return (
                        <li key={idx} className="position-relative">
                          <span>
                            <i
                              className={`fa-solid ${
                                isIncluded
                                  ? "fa-check check-icon"
                                  : "fa-xmark check-icon"
                              }`}
                            ></i>{" "}
                            {feature.name}
                          </span>

                          {description && (
                            <i
                              className="fa-regular fa-circle-question ms-2 text-muted"
                              style={{ cursor: "pointer" }}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={description}
                            ></i>
                          )}
                        </li>
                      );
                    })}
                </ul>

                {allFeatures.length > 5 && (
                  <div className="d-flex justify-content-center">
                    <button
                      onClick={() => handleShowAllFeatures(plan._id)}
                      className="feature-expand-button btn-sm mt-2 border-0 bg-transparent"
                      aria-label={showAllFeatures[plan._id] ? "Show less features" : "Show all features"}
                    >
                      <span className="feature-btn btn-sm btn-arrow btn-arrow-sm mt-4">
                        <i
                          className={`fa ${
                            showAllFeatures[plan._id] ? "fa-arrow-up" : "fa-arrow-down"
                          }`}
                        ></i>
                      </span>
                    </button>
                  </div>
                )}
                <div className="pricing-button">
                  <a href={plan.purchaseLink || "#"} target="_blank" rel="noopener noreferrer" className="pricing-btn btn-arrow btn-arrow-sm mt-3" >
                    Get Started Now
                    <span className="btn-arrow__text">
                      <span className="btn-arrow__icon">
                        <i className="fa-solid fa-arrow-right"></i>
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredCustomizedPlans.length > 0 && (
        <div className="table-responsive mt-5">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th className="table-heading">Plan</th>
                {isAnyGpuServer && <th className="table-heading">GPU Type</th>}
                {!isGpuDedicatedServerIndia && <th className="table-heading">Processor</th>}
                <th className="table-heading">RAM</th>
                <th className="table-heading">CPU</th>
                {isAnyGpuServer && <th className="table-heading">GPU RAM</th>}
                <th className="table-heading">Hard Disk</th>
                {!isGpuDedicatedServerIndia && <th className="table-heading">Bandwidth</th>}
                <th className="table-heading">Location</th>
                <th className="table-heading">Price(MO)</th>
                <th className="table-heading">Offer Price</th>
                <th className="table-heading">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomizedPlans.map((plan) => {
                const basePrice = calculatePrice(plan.price, duration);
                const price = basePrice * conversionRate;
                const symbol = currency === "INR" ? "₹" : "$";

                const baseCutOffPrice = calculatePrice(plan.cutOffPrice, duration);
                const cutOffPrice = baseCutOffPrice * conversionRate;

                return (
                  <tr key={plan._id}>
                    <td className="table-content">{plan.title}</td>
                    {isAnyGpuServer && (
                      <td className="table-content">{plan.gpuType || "N/A"}</td>
                    )}
                    {!isGpuDedicatedServerIndia && (
                      <td className="table-content">{plan.processor}</td>
                    )}
                    <td className="table-content">{plan.ram}</td>
                    <td className="table-content">{plan.cpu}</td>
                    {isAnyGpuServer && (
                      <td className="table-content">{plan.gpuRam || "N/A"}</td>
                    )}
                    <td className="table-content">{plan.hardDisk}</td>

                    {!isGpuDedicatedServerIndia && (
                      <td className="table-content">{plan.bandWidth}</td>
                    )}
                    <td className="table-content">{plan.location}</td>
                    <td className="table-content">{symbol}{price.toLocaleString()}</td>
                      {plan.discount > 0 ? (
                        <td className="table-content">
                            <del className="price-text"> {symbol} {Math.round(cutOffPrice).toLocaleString()} </del> <br></br>
                            <span className="text-success"> SAVE {plan.discount}% </span>
                        </td>
                      ) : (
                        <td className="table-content">
                          <span className="price-text"> {symbol} {Math.round(cutOffPrice).toLocaleString()} </span> <br></br>
                          <span className="text-danger"> No Disocunt</span>
                        </td>
                      )}
                    <td className="table-content">
                      <a href={plan.purchaseLink } target="_blank" rel="noopener noreferrer" className="btn btn-dark btn-sm fs-14 fw-medium rounded-pill py-2">
                        Buy
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
    </div>
  );
};

export default PlanList;